import { useEffect } from 'react';
import Page from '../../../components/page/Page';
import {
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Card,
  CardContent,
  Box,
  Grid,
} from '@mui/material';
import useThemeMediaQuery from '../../../../core/hooks/useThemeMediaQuery';
import { useNavigationCore } from '../../../../core/router/navigation';
import { useMeCore } from '../../../../core/modules/me';
import { useCompanyCore } from '../../../../core/modules/company';
import { useLocationReportCore } from '../../../../core/modules/location-report';
import InventoryIcon from '@mui/icons-material/Inventory';
import ArticleIcon from '@mui/icons-material/Article';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const LocationsReport = () => {
  const { user } = useMeCore();
  const { companyId } = useCompanyCore();
  const {
    locationsReport = [],
    loading,
    error,
    getLocationsReport,
  } = useLocationReportCore();
  const { goToWelcome } = useNavigationCore();

  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'));

  useEffect(() => {
    if (
      !user ||
      (!user.privileges.superAdmin && !user.privileges.readLocation)
    ) {
      goToWelcome();
    }
  }, [user, goToWelcome]);

  useEffect(() => {
    if (companyId) {
      getLocationsReport(companyId);
    }
  }, [companyId, getLocationsReport]);

  const LocationCard = ({ location }: { location: any }) => {
    const totalItems =
      location.container_count +
      location.articles +
      location.origin_container_count;
    const isEmpty = totalItems === 0;

    return (
      <Card
        sx={{
          height: '100%',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4,
          },
          border: isEmpty ? '2px solid #e0e0e0' : '2px solid #2196f3',
          backgroundColor: isEmpty ? '#fafafa' : '#fff',
        }}
      >
        <CardContent>
          {/* Header */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <LocationOnIcon color="primary" />
              <Typography variant="h6" fontWeight="bold" color="primary">
                {location.code}
              </Typography>
            </Box>
            <Chip
              label={location.is_free ? 'Libre' : 'Ocupada'}
              color={location.is_free ? 'success' : 'error'}
              variant="outlined"
              size="small"
            />
          </Box>

          {/* ID */}
          <Typography variant="body2" color="text.secondary" mb={2}>
            ID: {location.location_id}
          </Typography>

          {/* Content Stats */}
          <Box display="flex" flexDirection="column" gap={1.5}>
            {/* Contenedores */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box display="flex" alignItems="center" gap={1}>
                <InventoryIcon
                  fontSize="small"
                  color={location.container_count > 0 ? 'success' : 'disabled'}
                />
                <Typography variant="body2">Contenedores</Typography>
              </Box>
              <Typography
                variant="body2"
                fontWeight="bold"
                color={
                  location.container_count > 0
                    ? 'success.main'
                    : 'text.secondary'
                }
              >
                {location.container_count}
              </Typography>
            </Box>

            {/* Artículos */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box display="flex" alignItems="center" gap={1}>
                <ArticleIcon
                  fontSize="small"
                  color={location.articles > 0 ? 'info' : 'disabled'}
                />
                <Typography variant="body2">Artículos</Typography>
              </Box>
              <Typography
                variant="body2"
                fontWeight="bold"
                color={location.articles > 0 ? 'info.main' : 'text.secondary'}
              >
                {location.articles}
              </Typography>
            </Box>

            {/* Contenedores de Origen */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box display="flex" alignItems="center" gap={1}>
                <LocalShippingIcon
                  fontSize="small"
                  color={
                    location.origin_container_count > 0 ? 'warning' : 'disabled'
                  }
                />
                <Typography variant="body2">Cont. Origen</Typography>
              </Box>
              <Typography
                variant="body2"
                fontWeight="bold"
                color={
                  location.origin_container_count > 0
                    ? 'warning.main'
                    : 'text.secondary'
                }
              >
                {location.origin_container_count}
              </Typography>
            </Box>
          </Box>

          {/* Summary */}
          <Box mt={2} pt={2} borderTop={1} borderColor="divider">
            {isEmpty ? (
              <Typography
                variant="body2"
                color="text.secondary"
                fontStyle="italic"
                textAlign="center"
              >
                Ubicación vacía
              </Typography>
            ) : (
              <Typography
                variant="body2"
                color="text.primary"
                textAlign="center"
                fontWeight="medium"
              >
                Total: {totalItems} elemento{totalItems !== 1 ? 's' : ''}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Page
        header={
          <div className="p-24 sm:p-32 w-full border-b-1 bg-white">
            <Typography className="text-24 md:text-32 font-extrabold tracking-tight leading-none">
              Reporte de Ubicaciones
            </Typography>
          </div>
        }
        content={
          <div className="flex justify-center items-center h-full">
            <CircularProgress />
          </div>
        }
        scroll={isMobile ? 'normal' : 'content'}
      />
    );
  }

  if (error) {
    return (
      <Page
        header={
          <div className="p-24 sm:p-32 w-full border-b-1 bg-white">
            <Typography className="text-24 md:text-32 font-extrabold tracking-tight leading-none">
              Reporte de Ubicaciones
            </Typography>
          </div>
        }
        content={
          <div className="w-full my-16 px-24 md:px-32">
            <Alert severity="error" className="mb-16">
              {error}
            </Alert>
          </div>
        }
        scroll={isMobile ? 'normal' : 'content'}
      />
    );
  }

  // Separar ubicaciones libres y ocupadas
  const freeLocations = locationsReport.filter((location) => location.is_free);
  const occupiedLocations = locationsReport.filter(
    (location) => !location.is_free
  );

  return (
    <Page
      header={
        <div className="p-24 sm:p-32 w-full border-b-1 bg-white">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex flex-col items-center sm:items-start">
              <Typography className="text-24 md:text-32 font-extrabold tracking-tight leading-none">
                Reporte de Ubicaciones
              </Typography>
              <Typography
                className="text-14 font-medium"
                color="text.secondary"
              >
                {locationsReport.length} ubicación
                {locationsReport.length !== 1 ? 'es' : ''} encontrada
                {locationsReport.length !== 1 ? 's' : ''}
              </Typography>
            </div>

            {/* Stats */}
            <Box display="flex" gap={2}>
              <Chip
                label={`${freeLocations.length} Libres`}
                color="success"
                variant="outlined"
              />
              <Chip
                label={`${occupiedLocations.length} Ocupadas`}
                color="error"
                variant="outlined"
              />
            </Box>
          </div>
        </div>
      }
      content={
        <div className="w-full my-16 px-24 md:px-32">
          {/* Ubicaciones Ocupadas */}
          {occupiedLocations.length > 0 && (
            <Box mb={4}>
              <Typography
                variant="h6"
                fontWeight="bold"
                color="error.main"
                mb={2}
              >
                Ubicaciones Ocupadas ({occupiedLocations.length})
              </Typography>
              <Grid container spacing={3}>
                {occupiedLocations.map((location) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    lg={3}
                    key={location.location_id}
                  >
                    <LocationCard location={location} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Ubicaciones Libres */}
          {freeLocations.length > 0 && (
            <Box>
              <Typography
                variant="h6"
                fontWeight="bold"
                color="success.main"
                mb={2}
              >
                Ubicaciones Libres ({freeLocations.length})
              </Typography>
              <Grid container spacing={3}>
                {freeLocations.map((location) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    lg={3}
                    key={location.location_id}
                  >
                    <LocationCard location={location} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Mensaje si no hay datos */}
          {locationsReport.length === 0 && !loading && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                No se encontraron ubicaciones
              </Typography>
            </Paper>
          )}
        </div>
      }
      scroll={isMobile ? 'normal' : 'content'}
    />
  );
};

export default LocationsReport;
